/**
 * Componente de Card de Prazo Individual
 * Design moderno inspirado no sistema antigo
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
  RotateCcw,
  Eye,
  Edit3,
  Calendar,
  User,
  FileText,
  Tag,
  ChevronDown,
  ChevronUp,
  Copy,
  Globe
} from 'lucide-react';

const PrazoCard = ({
  prazo,
  onMarcarConcluido,
  onReverterConclusao,
  onVerDetalhes,
  onEditarPrazo,
  showActions = true
}) => {
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Configurações de cor por urgência - Azul Moderno Vibrante
  const urgenciaConfig = {
    hoje: {
      color: 'red',
      bg: 'bg-gradient-to-br from-red-50 to-red-100',
      border: 'border-red-300',
      borderLeft: 'border-l-red-600',
      text: 'text-red-700',
      badge: 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg font-bold',
      icon: AlertTriangle,
      label: '🚨 VENCE HOJE',
      glow: 'shadow-red-600/30',
      accent: 'from-red-600/15 to-red-700/10'
    },
    amanha: {
      color: 'orange',
      bg: 'bg-gradient-to-br from-orange-50 to-orange-100',
      border: 'border-orange-300',
      borderLeft: 'border-l-orange-600',
      text: 'text-orange-700',
      badge: 'bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-lg font-bold',
      icon: AlertCircle,
      label: '⚠️ 1 DIA',
      glow: 'shadow-orange-600/30',
      accent: 'from-orange-600/15 to-orange-700/10'
    },
    esta_semana: {
      color: 'blue',
      bg: 'bg-gradient-to-br from-blue-50 to-blue-100',
      border: 'border-blue-300',
      borderLeft: 'border-l-blue-600',
      text: 'text-blue-800',
      badge: 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-lg font-bold',
      icon: Clock,
      label: 'ESTA SEMANA',
      glow: 'shadow-blue-600/30',
      accent: 'from-blue-600/15 to-blue-700/10'
    },
    vencido: {
      color: 'red',
      bg: 'bg-gradient-to-br from-red-100 to-red-200',
      border: 'border-red-400',
      borderLeft: 'border-l-red-700',
      text: 'text-red-800',
      badge: 'bg-gradient-to-r from-red-700 to-red-800 text-white shadow-lg animate-pulse font-bold',
      icon: AlertTriangle,
      label: '⚠️ VENCIDO',
      glow: 'shadow-red-700/40',
      accent: 'from-red-700/20 to-red-800/15'
    },
    proximo_mes: {
      color: 'gray',
      bg: 'bg-gradient-to-br from-slate-50 to-slate-100',
      border: 'border-slate-300',
      borderLeft: 'border-l-slate-500',
      text: 'text-slate-700',
      badge: 'bg-gradient-to-r from-slate-600 to-slate-700 text-white shadow-md font-bold',
      icon: Calendar,
      label: 'PRÓXIMO MÊS',
      glow: 'shadow-slate-600/20',
      accent: 'from-slate-600/15 to-slate-700/10'
    }
  };

  // Determinar urgência baseada nos dias restantes
  const getUrgenciaAtual = () => {
    if (prazo.urgencia === 'vencido' || prazo.diasRestantes < 0) return 'vencido';
    if (prazo.diasRestantes === 0 || prazo.urgencia === 'hoje') return 'hoje';
    if (prazo.diasRestantes === 1) return 'amanha';
    if (prazo.urgencia === 'esta_semana' || prazo.diasRestantes <= 7) return 'esta_semana';
    return 'proximo_mes';
  };

  const urgenciaAtual = getUrgenciaAtual();
  const config = urgenciaConfig[urgenciaAtual] || urgenciaConfig.proximo_mes;
  const UrgenciaIcon = config.icon;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getDiasRestantesText = (dias, diasTotais) => {
    const totalText = diasTotais ? ` de ${diasTotais}` : '';

    if (dias < 0) return `${Math.abs(dias)} dias em atraso${totalText}`;
    if (dias === 0) return `Vence hoje${totalText}`;
    if (dias === 1) return `1${totalText} dia restante`;
    return `${dias}${totalText} dias restantes`;
  };

  const formatDataPublicacao = (dataString) => {
    if (!dataString) return 'N/A';
    return new Date(dataString).toLocaleDateString('pt-BR');
  };

  const getTipoAtoFormatado = (tipoAto) => {
    const tipos = {
      'citação': 'Citação',
      'intimação': 'Intimação',
      'sentença': 'Sentença',
      'decisão': 'Decisão',
      'despacho': 'Despacho',
      'acordão': 'Acórdão',
      'manifestação': 'Manifestação',
      'contestação': 'Contestação',
      'recurso': 'Recurso',
      'impugnação': 'Impugnação'
    };

    return tipos[tipoAto?.toLowerCase()] || tipoAto || 'Comunicação';
  };

  const handleMarcarConcluido = async () => {
    if (!onMarcarConcluido) return;
    
    setLoading(true);
    try {
      await onMarcarConcluido(prazo);
    } catch (error) {
      console.error('Erro ao marcar como concluído:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReverterConclusao = async () => {
    if (!onReverterConclusao) return;
    
    setLoading(true);
    try {
      await onReverterConclusao(prazo);
    } catch (error) {
      console.error('Erro ao reverter conclusão:', error);
    } finally {
      setLoading(false);
    }
  };

  const isConcluido = prazo.status === 'concluido';

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      // Feedback visual simples
      const button = document.activeElement;
      const originalText = button.innerHTML;
      button.innerHTML = type === 'processo' ? '✅ Copiado!' : '✅ Link copiado!';
      setTimeout(() => {
        button.innerHTML = originalText;
      }, 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const getPJeLink = (numeroProcesso, tribunal) => {
    // Links básicos para os principais tribunais
    const tribunalLinks = {
      'TJRO': `https://pje.tjro.jus.br/pje/ConsultaPublica/listView.seam?ca=${numeroProcesso}`,
      'TRT23': `https://pje.trt23.jus.br/consultaprocessual/pages/consultas/DetalhaProcesso.jsf?numeroProcesso=${numeroProcesso}`,
      'TJSP': `https://esaj.tjsp.jus.br/cpopg/show.do?processo.numero=${numeroProcesso}`,
      'TJRJ': `http://www4.tjrj.jus.br/consultaProcessoWebV2/consultaProc.do?v=2&numProcesso=${numeroProcesso}`,
      'TRF1': `https://processual.trf1.jus.br/consultaProcessual/processo.php?proc=${numeroProcesso}`,
      'STJ': `https://scon.stj.jus.br/SCON/pesquisar.jsp?newsession=yes&tipo_visualizacao=RESUMO&b=ACOR&p=true&t=JURIDICO&l=10&i=1&c=&termo=${numeroProcesso}`
    };

    return tribunalLinks[tribunal] || `https://www.google.com/search?q=${numeroProcesso}+${tribunal}+site:jus.br`;
  };

  return (
    <>
      <div className={`
        relative rounded-xl border-l-4 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 group
        ${isConcluido 
          ? 'border-l-slate-400 opacity-75 bg-gradient-to-br from-slate-50 to-slate-100' 
          : `${config.borderLeft} ${config.bg} hover:${config.glow}`
        }
      `}>
      {/* Efeito de glow sutil no hover */}
      {!isConcluido && (
        <div className={`absolute -inset-0.5 bg-gradient-to-r ${config.accent} rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-300`} />
      )}
      
      <div className="relative p-3 sm:p-4">
        {/* Header Compacto - Sempre Visível */}
        <div
          className="flex items-start justify-between cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className={`p-1.5 sm:p-2 rounded-lg ${isConcluido ? 'bg-slate-100' : 'bg-white/50 backdrop-blur-sm'} group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
              <UrgenciaIcon
                size={16}
                className={isConcluido ? 'text-slate-500' : config.text}
              />
            </div>
            <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2">
              {/* Linha 1: Título e Badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`font-bold text-sm sm:text-base ${isConcluido ? 'text-slate-600' : 'text-slate-900'}`}>
                  {getTipoAtoFormatado(prazo.tipoAto || prazo.tipo)}
                </h3>
                {/* Status Badge */}
                {isConcluido ? (
                  <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-slate-600 to-slate-700 text-white shadow-md">
                    <CheckCircle size={10} className="mr-1" />
                    <span className="hidden sm:inline">CONCLUÍDO</span>
                    <span className="sm:hidden">✅</span>
                  </span>
                ) : (
                  <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${config.badge}`}>
                    <span className="hidden sm:inline">{config.label}</span>
                    <span className="sm:hidden">{config.label.split(' ')[0]}</span>
                  </span>
                )}
              </div>

              {/* Linha 2: Número do Processo + Tribunal */}
              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                <span className="font-mono font-semibold text-slate-700 bg-slate-100 px-2 py-1 rounded border text-xs">
                  {prazo.numeroProcesso}
                </span>
                {prazo.tribunal && (
                  <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200 text-xs">
                    🏛️ {prazo.tribunal}
                  </span>
                )}
              </div>

              {/* Linha 3: Cliente (SEMPRE VISÍVEL se houver) */}
              {prazo.cliente && prazo.cliente !== 'N/A' && (
                <div className="text-xs sm:text-sm">
                  <span className="inline-flex items-center gap-1 font-semibold text-indigo-700 bg-indigo-50 px-2 py-1 rounded border border-indigo-200">
                    👤 {prazo.cliente}
                  </span>
                </div>
              )}

              {/* Linha 4: Dias Restantes + Vencimento */}
              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                <span className={`font-bold px-2 sm:px-3 py-1 rounded-lg shadow-sm text-xs ${
                  prazo.diasRestantes < 0 ? 'bg-red-100 text-red-800 border border-red-200' :
                  prazo.diasRestantes === 0 ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                  prazo.diasRestantes === 1 ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                  'bg-slate-100 text-slate-800 border border-slate-200'
                }`}>
                  {getDiasRestantesText(prazo.diasRestantes, prazo.dias)}
                </span>
                <span className="text-slate-600 font-medium">
                  📅 <span className="font-bold text-slate-900">{formatDate(prazo.dataVencimento)}</span>
                </span>
              </div>

              {/* Linha 5: Prévia do Conteúdo da Publicação */}
              {(prazo.descricao || prazo.textoCompleto) && (
                <div className="text-xs sm:text-sm text-slate-700 bg-slate-50 p-2 sm:p-3 rounded-lg border">
                  <div className="font-medium text-slate-800 mb-2 flex items-center gap-1">
                    📄 <span className="hidden sm:inline">Conteúdo da Publicação:</span>
                    <span className="sm:hidden">Conteúdo:</span>
                  </div>
                  <div className="text-slate-700 text-xs sm:text-sm line-clamp-3">
                    {prazo.textoCompleto || prazo.descricao}
                  </div>
                  <div className="text-xs text-blue-600 mt-2 font-medium">
                    💡 Clique em "Ver Detalhes" para ler o conteúdo completo
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botão de Expansão */}
          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
            <div className="p-1.5 sm:p-2 rounded-lg bg-white/50 group-hover:bg-white/80 transition-colors">
              {isExpanded ? (
                <ChevronUp size={14} className="text-slate-400" />
              ) : (
                <ChevronDown size={14} className="text-slate-400" />
              )}
            </div>
          </div>
        </div>

        {/* Seção Expandida - Detalhes Adicionais */}
        {isExpanded && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-200 space-y-2 sm:space-y-3">
            {/* Data de publicação */}
            {prazo.dataPublicacao && (
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <div className="p-1.5 sm:p-2 rounded-lg bg-green-600 shadow-sm">
                  <FileText size={14} className="text-white" />
                </div>
                <span className="text-slate-700 font-medium">
                  <span className="hidden sm:inline">Publicado em: </span>
                  <span className="font-bold text-slate-900">{formatDataPublicacao(prazo.dataPublicacao)}</span>
                </span>
              </div>
            )}

            {/* Observações do usuário */}
            {prazo.observacoes && (
              <div className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm">
                <div className="p-1.5 sm:p-2 rounded-lg bg-cyan-600 shadow-sm mt-0.5">
                  <Tag size={14} className="text-white" />
                </div>
                <span className="text-cyan-800 italic font-bold">{prazo.observacoes}</span>
              </div>
            )}

            {/* Tribunal */}
            {prazo.tribunal && (
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <div className="p-1.5 sm:p-2 rounded-lg bg-emerald-600 shadow-sm">
                  <ExternalLink size={14} className="text-white" />
                </div>
                <span className="text-slate-900 font-bold">{prazo.tribunal}</span>
              </div>
            )}

            {/* Tags */}
            {prazo.tags && prazo.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {prazo.tags.slice(0, 5).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-lg text-xs bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 border border-slate-300"
                  >
                    <Tag size={8} className="mr-1" />
                    {tag}
                  </span>
                ))}
                {prazo.tags.length > 5 && (
                  <span className="text-xs text-slate-500 px-2 py-1">
                    +{prazo.tags.length - 5} mais
                  </span>
                )}
              </div>
            )}

            {/* Checklist de Ações */}
            {!isConcluido && (
              <div className="p-2 sm:p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1">
                  ✅ <span className="hidden sm:inline">Checklist de Ações:</span>
                  <span className="sm:hidden">Checklist:</span>
                </p>
                <ul className="text-xs text-amber-700 space-y-2">
                  {/* Ações padrão para todo prazo */}
                  <li className="flex items-center gap-2">
                    <input type="checkbox" className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                    <span>Verificar processo no tribunal</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <input type="checkbox" className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                    <span>Preparar peça/manifestação</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <input type="checkbox" className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                    <span>Protocolar no sistema</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <input type="checkbox" className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                    <span>Confirmar protocolo</span>
                  </li>
                  {/* Ações sugeridas pela IA (se houver) */}
                  {prazo.acoesSugeridas && prazo.acoesSugeridas.length > 0 && (
                    <>
                      <li className="pt-2 border-t border-amber-200 text-amber-800 font-medium">
                        🤖 Sugestões da IA:
                      </li>
                      {prazo.acoesSugeridas.map((acao, index) => (
                        <li key={index} className="flex items-center gap-2 pl-2">
                          <input type="checkbox" className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                          <span className="break-words">{acao}</span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
            )}

            {/* Alerta de segurança jurídica */}
            {!isConcluido && (
              <div className="p-2 sm:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-xs">
                <div className="flex items-center gap-1 text-blue-700">
                  <AlertCircle size={12} />
                  <span className="font-medium">⚖️ <span className="hidden sm:inline">Segurança Jurídica:</span></span>
                </div>
                <p className="text-blue-600 mt-1">
                  <span className="hidden sm:inline">Cálculo não considera feriados. Sempre confirme no calendário oficial do tribunal.</span>
                  <span className="sm:hidden">Confirme feriados no tribunal.</span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Botões de Ação - Sempre visíveis e bem alinhados */}
        {showActions && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            {/* Primeira linha: Botões de processo */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(prazo.numeroProcesso, 'processo');
                }}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold text-purple-700 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-300 rounded-lg hover:from-purple-100 hover:to-purple-200 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                <Copy size={12} />
                <span className="hidden sm:inline">Copiar Processo</span>
                <span className="sm:hidden">📋</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const link = getPJeLink(prazo.numeroProcesso, prazo.tribunal);
                  window.open(link, '_blank');
                }}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold text-emerald-700 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-300 rounded-lg hover:from-emerald-100 hover:to-emerald-200 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                <Globe size={12} />
                <span className="hidden sm:inline">Abrir no PJe</span>
                <span className="sm:hidden">🌐</span>
              </button>
            </div>

            {/* Segunda linha: Botões de ação principais */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {isConcluido ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReverterConclusao();
                  }}
                  disabled={loading}
                  className="col-span-1 sm:col-span-2 lg:col-span-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-slate-700 bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-300 rounded-lg hover:from-slate-200 hover:to-slate-300 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  <RotateCcw size={14} />
                  <span className="hidden sm:inline">Reverter</span>
                  <span className="sm:hidden">↩️</span>
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarcarConcluido();
                  }}
                  disabled={loading}
                  className="col-span-1 sm:col-span-2 lg:col-span-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-green-600 to-green-700 border border-green-500 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  <CheckCircle size={14} />
                  <span className="hidden sm:inline">{loading ? 'Salvando...' : 'Concluir'}</span>
                  <span className="sm:hidden">{loading ? '⏳' : '✅'}</span>
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onVerDetalhes && onVerDetalhes(prazo);
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-blue-700 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-300 rounded-lg hover:from-blue-100 hover:to-blue-200 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                <Eye size={14} />
                <span className="hidden sm:inline">Ver Detalhes</span>
                <span className="sm:hidden">👁️</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditarPrazo && onEditarPrazo(prazo);
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-indigo-700 bg-gradient-to-r from-indigo-50 to-indigo-100 border border-indigo-300 rounded-lg hover:from-indigo-100 hover:to-indigo-200 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                <Edit3 size={14} />
                <span className="hidden sm:inline">Editar</span>
                <span className="sm:hidden">✏️</span>
              </button>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 rounded-xl flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default PrazoCard;
